import React, { useEffect, useRef, useState } from 'react';
import { PacmanGame, PacmanGameState } from './PacmanGame';
import DebugInfo from './ui/DebugInfo';
import GameStats from './ui/GameStats';
import GameTitle from './ui/GameTitle';
import GameStateOverlay from './ui/GameStateOverlay';
import ControlsInfo from './ui/ControlsInfo';
import { EntityManager } from '../../engine/ecs/core/EntityManager';
import { ComponentManager } from '../../engine/ecs/core/ComponentManager';
import { EventManager } from '../../engine/events/EventManager';
import MobileControls from '../../engine/input/MobileControls';

interface PacmanGameWrapperProps {
    width?: number;
    height?: number;
    onLevelComplete?: () => void;
    displayLevelStart?: number;
    mazeMode?: 'fixed' | 'random';
    mazeSeed?: string;
}

const PacmanGameWrapper: React.FC<PacmanGameWrapperProps> = ({
    width = 672,
    height = 744,
    onLevelComplete,
    displayLevelStart = 1,
    mazeMode = 'fixed',
    mazeSeed,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const gameRef = useRef<PacmanGame | null>(null);
    const animationRef = useRef<number>();
    const levelCompleteFiredRef = useRef<boolean>(false);
    const [gameState, setGameState] = useState<PacmanGameState>(PacmanGameState.MainMenu);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [level, setLevel] = useState(1);
    const [gameStarted, setGameStarted] = useState(false);
    const [fps, setFps] = useState(0);
    const [entityCount, setEntityCount] = useState(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Style canvas for better appearance
        canvas.style.border = '3px solid #1E90FF';
        canvas.style.backgroundColor = '#000000';
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto';

        // Cleanup old game instance if it exists
        if (gameRef.current) {
            console.log('[PacmanGameWrapper] Cleaning up old game instance');
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            EntityManager.getInstance().clear();
            ComponentManager.getInstance().clear();
            EventManager.getInstance().clear();
        }

        // Initialize the game with global level offset and maze options
        console.log(`[PacmanGameWrapper] Creating new game with mazeMode: ${mazeMode}, mazeSeed: ${mazeSeed}`);
        gameRef.current = new PacmanGame(canvas, { 
            levelOffset: displayLevelStart - 1,
            mazeMode,
            mazeSeed
        });
        
        // Reset game state
        setGameStarted(false);
        setGameState(PacmanGameState.MainMenu);
        setScore(0);
        setLives(3);
        setLevel(1);
        levelCompleteFiredRef.current = false;

        return () => {
            // Cleanup when component unmounts (game switching)
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (gameRef.current) {
                // Clear singleton managers to prevent conflicts with other games

                EntityManager.getInstance().clear();
                ComponentManager.getInstance().clear();
                EventManager.getInstance().clear();

                console.log('PacmanGameWrapper: Cleaned up singleton state on unmount');
            }
        };
    }, [width, height, displayLevelStart, mazeMode, mazeSeed]);

    const startGame = () => {
        if (gameRef.current && !gameStarted) {
            setGameStarted(true);
            gameRef.current.start();

            // Update UI state based on game state - reduced frequency to prevent race conditions
            let frameCount = 0;
            const updateUI = () => {
                if (gameRef.current) {
                    // Update every 10 frames (6 times per second instead of 60) to reduce race conditions
                    if (frameCount % 10 === 0) {
                        const newGameState = gameRef.current.getGameState();
                        const newScore = gameRef.current.getScore();
                        const newLives = gameRef.current.getLives();
                        const newLevel = gameRef.current.getLevel();
                        const newFps = gameRef.current.getFPS();
                        const newEntityCount = gameRef.current.getEntityCount();

                        // Only update if values have changed to prevent unnecessary re-renders
                        setGameState(prevState => {
                            if (newGameState !== prevState) {
                                if (
                                    newGameState === PacmanGameState.LevelCompleteScreen &&
                                    !levelCompleteFiredRef.current
                                ) {
                                    levelCompleteFiredRef.current = true;
                                    onLevelComplete?.();
                                }
                                return newGameState;
                            }
                            return prevState;
                        });
                        setScore(prevScore => newScore !== prevScore ? newScore : prevScore);
                        setLives(prevLives => newLives !== prevLives ? newLives : prevLives);
                        setLevel(prevLevel => newLevel !== prevLevel ? newLevel : prevLevel);
                        setFps(prevFps => Math.abs(newFps - prevFps) > 1 ? newFps : prevFps);
                        setEntityCount(prevCount => newEntityCount !== prevCount ? newEntityCount : prevCount);
                    }
                    frameCount++;
                }
                animationRef.current = requestAnimationFrame(updateUI);
            };
            updateUI();
        }
    };

    const resetGame = () => {
        if (gameRef.current) {
            gameRef.current = new PacmanGame(canvasRef.current!, { 
                levelOffset: displayLevelStart - 1,
                mazeMode,
                mazeSeed
            });
            setGameStarted(false);
            setGameState(PacmanGameState.MainMenu);
            setScore(0);
            setLives(3);
            setLevel(1);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        }
    };

    const startNextLevel = () => {
        // Progressive mode: advance to the next world
        onLevelComplete?.();
    };

    return (
        <>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#000',
                minHeight: '100vh',
                padding: '20px',
                fontFamily: 'monospace'
            }}>
                {/* Game Title */}
                <GameTitle title="Space Dotman" />

                {/* Game Stats */}
                <GameStats
                    score={score}
                    level={displayLevelStart + (level - 1)}
                    lives={lives}
                />

                {/* Game Canvas */}
                <div style={{ position: 'relative' }}>
                    <canvas
                        ref={canvasRef}
                        style={{
                            border: '4px solid #1E90FF',
                            borderRadius: '8px',
                            boxShadow: '0 0 20px rgba(30,144,255,0.5)'
                        }}
                    />

                    {/* Debug Info - Left Side of Canvas */}
                    <DebugInfo
                        fps={fps}
                        entityCount={entityCount}
                        level={displayLevelStart + (level - 1)}
                        score={score}
                        lives={lives}
                        isVisible={gameStarted}
                    />

                    {/* Controls Info - Right Side of Canvas */}
                    <ControlsInfo isPositioned={true} />

                    {/* Game State Overlay */}
                    <GameStateOverlay
                        gameState={gameState}
                        gameStarted={gameStarted}
                        score={score}
                        level={displayLevelStart + (level - 1)}
                        onStartGame={startGame}
                        onResetGame={resetGame}
                        onStartNextLevel={startNextLevel}
                    />
                </div>
            </div>
            <MobileControls />
        </>
    );
};

export default PacmanGameWrapper;